/**
 * Handles all the calculations for invoice items.
 * Used inside of the VendorInvoiceTotals service.
 */
export function InvoiceItemTotalsService (
  bigDecimalApi,
  sessionObjAPI
) {
  var InvoiceItemTotals = function (invoice) {
    this.invoice = invoice;
    this.info = sessionObjAPI.currency();
    this.company = sessionObjAPI.company();
  };

  var calculateMultiTax = function (taxRates, total, item, hasXero) {
    var rateSummation = BigDecimal.ZERO;
    var tax           = BigDecimal.ZERO;

    // Rounding is handled a bit differently for Xero
    // users than it is for others. We add the tax rates
    // together, multiply the subtotal of the current
    // item by the summation of the tax rates, then round.
    if (hasXero) {

      angular.forEach(taxRates, function (rate) {
        rateSummation = rateSummation.add(rate.labor_gst_free && item.product_type === 'J' ? BigDecimal.ZERO: rate.tax_rate);
      });

      tax = total.multiply(rateSummation.movePointLeft(2)).setScale(2, BigDecimal.ROUND_HALF_UP);
    } else {

      // If the tax rate has labor_gst_free, the result is zero,
      // otherwise calculate like normal.
      angular.forEach(taxRates, function (rate) {
        rate.multi_tax_item_result = (item.product_type === 'J' && rate.labor_gst_free) ?
          BigDecimal.ZERO:
          total.multiply(rate.tax_rate.movePointLeft(2)).setScale(2, BigDecimal.ROUND_HALF_UP);
      });

      angular.forEach(taxRates, function (rate) {
        tax = tax.add(rate.multi_tax_item_result);
      });
    }

    return tax.toString();
  };

  var calculateMultiTaxTotal = function (taxRates, total, item) {
    // Calculate each tax group item separately,
    // then add each to the total.
    angular.forEach(taxRates, function (rate) {
      rate.multi_tax_total_item_result = (item.product_type === 'J' && rate.labor_gst_free) ?
        BigDecimal.ZERO:
        total.multiply(rate.tax_rate.movePointLeft(2)).setScale(2, BigDecimal.ROUND_HALF_UP);
    });

    angular.forEach(taxRates, function (rate) {
      total = total.add(rate.multi_tax_total_item_result);
    });

    return total.toString();
  };

  var calculateSalesTax = function (taxRate, total) {
    return total.multiply(taxRate.movePointLeft(2))
      .setScale(2, BigDecimal.prototype.ROUND_HALF_UP).toString();
  };

  var calculateSalesTaxTotal = function (taxRate, total) {

    // Calculate tax like
    // normal using the predefined sales taxRate.
    return total
        .multiply(taxRate.movePointLeft(2))
          .setScale(2, BigDecimal.prototype.ROUND_HALF_UP)
            .add(total)
              .setScale(2, BigDecimal.prototype.ROUND_HALF_UP).toString();
  };

  var extractSalesTax = function (taxRate, total) {
    const subtotal = total.subtract(
      total.setScale(4, BigDecimal.ROUND_HALF_UP)
      .divide(
        BigDecimal.ONE.add(taxRate.movePointLeft(2))
        .setScale(4, BigDecimal.ROUND_HALF_UP)
      )
    );

    return subtotal.toString();
  };

  var getTaxRates = function (taxGroup) {
    var taxRates = taxGroup.tax_group_items_attributes;

    angular.forEach(taxRates, function (rate) {
      rate.tax_rate = bigDecimalApi.get(rate.tax_rate).setScale(2, BigDecimal.ROUND_HALF_UP);
    });

    return taxRates;
  };

  /**
   * Calculates the RRP (adjusted retail price) of a
   * single item (if index is provided), or all
   * invoice items.
   * @param {number} index the index of the item to calculate for.
   * @return {void}
   */
  InvoiceItemTotals.prototype.calculateRRP = function (index) {
    const self = this;

    // if an index was provided, calculate for
    // just that item.
    if (index) {
      self.invoice.invoice_items_attributes[index].adjusted_retail_price = self.singleItemTotal(self.invoice.invoice_items_attributes[index]);
    }

    // else, calculate for all items.
    else {
      self.invoice.invoice_items_attributes.forEach(item => {
        item.adjusted_retail_price = self.singleItemTotal(item);
      });
    }
  };

  /**
   * Determines whether or not the invoice is closed based on status
   * @return {boolean} true if closed, false if open
   */
  InvoiceItemTotals.prototype.closed = function () {
    var self = this;

    return self.invoice.invoice_status !== 'O';
  };

  /**
   * Calculates the item's total discount amount, whether
   * it is of type (C)ash or (P)ercentage.
   * @param {object} item The invoice item object.
   * @return {string} Discount amount.
   */
  InvoiceItemTotals.prototype.discountTotal = function (item) {
    const quantity = bigDecimalApi.get(item.quantity);
    const discount = bigDecimalApi.get(item.discount);
    const price    = bigDecimalApi.get(item.adjusted_retail_price);

    if (item.discount) {
      if (item.discount_type === 'C') {
        return discount
          .multiply(quantity)
          .toString();
      }
      else if (item.discount_type === 'P') {
        return price
          .multiply(discount.movePointLeft(2))
          .setScale(2, BigDecimal.ROUND_HALF_UP)
          .multiply(quantity)
          .toString();
      }
    }

    return BigDecimal.ZERO.toString();
  };

  /**
   * Calculates the total of the invoice item using
   * a quantity of 1.
   * @param {object} item The invoice item to calculate the total.
   * @return {string}     The total of the invoice item using a quantity of 1.
   */
  InvoiceItemTotals.prototype.singleItemTotal = function (item = {}) {

    // when calculating the RRP, we want to preserve the
    // product's original value and ignore any product
    // or customer discounts. To do this, we overwrite
    // the unit_price with the retail_price to be used
    // in the this.total() function's calculation. However,
    // if the unit price has been adjusted and is now
    // greater than the RRP, we will recalculate the RRP
    // using the unit price.
    if (item.adjusted_retail_price && (Number(item.unit_price) > Number(item.adjusted_retail_price))) {
      item.product_retail_price = item.unit_price;
    }

    return this.total({
      ...item,
      quantity  : 1,
      unit_price: item.product_retail_price
    });
  };

  /**
   * Calculates the total of the invoice item
   * @param  {object} item The invoice item to calculate the total
   * @return {string}      The total of the invoice item
   */
  InvoiceItemTotals.prototype.total = function (item) {
    const self = this;

    const quantity  = bigDecimalApi.get(item.quantity, 2);
    const taxRate   = bigDecimalApi.get(item.sales_tax_rate, 2);
    const unitPrice = bigDecimalApi.get(item.unit_price, 2);

    const total = unitPrice.multiply(quantity, 2).setScale(2, BigDecimal.ROUND_HALF_UP);

    let taxRates;

    if (self.company.use_multi_tax) {
      taxRates = getTaxRates(self.invoice.tax_group);
    }

    // If invoice is closed, just return the server value
    if (self.closed()) {
      return item.amount || BigDecimal.ZERO.toString();
    }

    // If the product is tax free, the price includes
    // tax, the invoice is internal, or the customer
    // is a tax free customer, return the total
    if (item.gst_free || self.info.price_includes_tax || self.type('internal') || self.invoice.tax_free) {
      return total.toString();
    }

    // If not, add in the tax to the total
    else {
      return self.company.use_multi_tax ? calculateMultiTaxTotal(taxRates, total, item): calculateSalesTaxTotal(taxRate, total);
    }
  };

  /**
   * Calculates the tax of the invoice item
   * @param  {object} item The invoice item to calculate the tax
   * @return {string}      The total tax of the invoice item
   */
  InvoiceItemTotals.prototype.tax = function(item) {
    const self = this;

    let tax = bigDecimalApi.get();

    const quantity  = bigDecimalApi.get(item.quantity, 2);
    const taxRate   = bigDecimalApi.get(item.sales_tax_rate, 2);
    const unitPrice = bigDecimalApi.get(item.unit_price, 2);

    const total = unitPrice.multiply(quantity).setScale(2, BigDecimal.ROUND_HALF_UP);

    let taxRates;

    if (self.company.use_multi_tax) {
      taxRates = getTaxRates(self.invoice.tax_group);
    }

    // If invoice is closed, just return the server value
    if (self.closed()) {
      return item.gst || BigDecimal.ZERO.toString();
    }

    // Don't include tax if product is tax free,
    // invoice is tax free, or invoice type is
    // internal
    else if (item.gst_free || self.invoice.tax_free || self.type('internal')) {
      return BigDecimal.ZERO.toString();
    }

    // If the product includes tax, extract out the tax from the total
    else if (self.info.price_includes_tax) {
      tax = extractSalesTax(taxRate, total);
    }

    // If not then just calculate it based on the tax rate
    else {
      tax = self.company.use_multi_tax ? calculateMultiTax(taxRates, total, item, self.company.has_xero): calculateSalesTax(taxRate, total);
    }

    return tax;
  };

  /**
   * Determines if the invoice matches the specified
   * type
   * @param  {string} type    The type of invoice being checked
   * @return {boolean}        Is the invoice the specified type
   */
  InvoiceItemTotals.prototype.type = function (type) {
    var self = this;
    var typeMap = {
      credit  : 'C',
      quote   : 'Q',
      invoice : 'I',
      internal: 'U'
    };

    return typeMap[type] === self.invoice.invoice_type;
  };

  return InvoiceItemTotals;
}